import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ObservableInput } from 'observable-input';
import { combineLatest, fromEvent, merge, Observable, Subject } from 'rxjs';
import {
  filter,
  map,
  startWith,
  switchMap,
  throttleTime,
} from 'rxjs/operators';
import { ChangeService } from '../core/change.service';
import { EventsService } from '../core/events.service';
import { replayLast } from '../rx/util';
import {
  AnchoredPosition,
  ANCHORED_POSITIONS,
  Box,
  getOrigin,
} from './callout-position';

/**
 * @deprecated Use riv-callout instead.
 * @see https://rivethealth.github.io/design-system/?path=/story/lib-modal-callout--example
 */
@Component({
  selector: 'deprecated-callout',
  templateUrl: './callout.component.html',
  styleUrls: ['./callout.component.scss'],
  providers: [ChangeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeprecatedCalloutComponent implements AfterViewInit {
  @ObservableInput()
  @Input()
  anchor!: Observable<Element | ClientRect | DOMRect>;

  @Input()
  calloutRight?: boolean = true;

  @ObservableInput()
  @Input()
  anchoredElement: Observable<Element | ClientRect | DOMRect>;

  @ObservableInput()
  @Input('isModal')
  _modal!: Observable<boolean>;
  modal = this._modal.pipe(startWith(true));

  @ObservableInput()
  @Input('preferredPosition')
  _position!: Observable<AnchoredPosition>;
  position = this._position.pipe(startWith(AnchoredPosition.BOTTOM_RIGHT));

  @ObservableInput()
  @Input('allowedPositions')
  _allowedPositions!: Observable<AnchoredPosition[]>;
  allowedAnchorPositions = this._allowedPositions.pipe(
    startWith(ANCHORED_POSITIONS),
  );

  @ObservableInput()
  @Input('fallbackDirection')
  _direction!: Observable<'cw' | 'ccw'>;
  direction = this._direction.pipe(startWith('ccw' as 'ccw' | 'cw'));

  @Input()
  showShadow: boolean = false;

  @Input()
  scrollable: boolean = true;

  @Input()
  calloutHeight: number | null = null;

  @Input()
  cardStyling: boolean = false;

  private readonly afterViewInit = new Subject<null>();

  @ViewChild('scrim', { static: false }) scrim: ElementRef<HTMLElement>;
  private readonly scrimClick = this.afterViewInit.pipe(
    switchMap(() =>
      (<Observable<MouseEvent>>(
        fromEvent(this.scrim.nativeElement, 'click')
      )).pipe(
        filter((evt: MouseEvent) => evt.target === this.scrim.nativeElement),
        map(() => {
          return null;
        }),
      ),
    ),
  );

  readonly resize = this.eventsService.resize.pipe(startWith(null));

  readonly windowSize = this.resize.pipe(
    map(() => ({ width: window.innerWidth, height: window.innerHeight })),
  );

  readonly anchorBounds: Observable<Box> = combineLatest([
    this.anchor,
    this.resize,
  ]).pipe(
    map(([anchor]) =>
      anchor instanceof Element ? anchor.getBoundingClientRect() : anchor,
    ),
    map(anchor => ({
      x: anchor.left,
      y: anchor.top,
      width: anchor.right - anchor.left,
      height: anchor.bottom - anchor.top,
    })),
  );

  readonly calloutSize = new Subject<{ width: number; height: number }>();

  readonly calloutAdjustedSize: Observable<{
    width: number;
    height: number;
  }> = this.calloutSize.pipe(
    map(size => ({
      ...size,
      height: Math.min(
        size.height,
        this.calloutHeight || Number.POSITIVE_INFINITY,
      ),
    })),
  );

  private readonly calloutOrigin = combineLatest([
    this.anchorBounds,
    this.calloutAdjustedSize,
    this.windowSize,
    this.position,
    this.allowedAnchorPositions,
    this.direction,
  ]).pipe(
    throttleTime(50),
    map(([anchor, size, windowSize, position, allowedPositions, direction]) =>
      getOrigin(
        anchor,
        size,
        windowSize,
        position,
        allowedPositions,
        direction,
      ),
    ),
    replayLast(),
  );

  readonly top = this.calloutOrigin.pipe(map(origin => origin.y));

  element: any = null;

  readonly left = this.calloutOrigin.pipe(
    map(origin => {
      if (this.element === null) {
        return origin.x;
      }
      const right = (
        this.element instanceof Element
          ? this.element.getBoundingClientRect()
          : this.element
      ).right;
      const left = (this.element instanceof Element
        ? this.element.getBoundingClientRect()
        : this.element,
      this.element instanceof Element
        ? this.element.getBoundingClientRect()
        : this.element).left;
      return !this.calloutRight ? window.innerWidth - (right - left) : origin.x;
    }),
  );

  @Output()
  readonly actualPosition = this.calloutOrigin.pipe(
    map(origin => origin.position),
  );

  readonly closeTrigger = new Subject<null>();

  @Output()
  close: Observable<null> = merge(this.scrimClick, this.closeTrigger);

  constructor(
    private readonly eventsService: EventsService,
    changeService: ChangeService,
  ) {
    if (this.anchoredElement) {
      changeService.listen(
        this.anchoredElement,
        element => (this.element = element),
      );
    }
  }

  ngAfterViewInit() {
    this.afterViewInit.next(null);
  }

  readonly height: Observable<number | null> = combineLatest([
    this.anchorBounds,
    this.calloutSize,
    this.windowSize,
    this.calloutOrigin,
  ]).pipe(
    map(([anchor, calloutSize, windowSize, origin]) => {
      if (!this.scrollable) return null;

      if (
        (origin.position == AnchoredPosition.BOTTOM_CENTER ||
          origin.position == AnchoredPosition.BOTTOM_LEFT ||
          origin.position == AnchoredPosition.BOTTOM_RIGHT) &&
        anchor.y + anchor.height + calloutSize.height > windowSize.height
      ) {
        return windowSize.height - anchor.y - anchor.height;
      }

      if (
        (origin.position == AnchoredPosition.TOP_CENTER ||
          origin.position == AnchoredPosition.TOP_LEFT ||
          origin.position == AnchoredPosition.TOP_RIGHT) &&
        anchor.y - calloutSize.height < 0
      ) {
        return anchor.y - 4;
      }
      return Math.min(
        this.calloutHeight || Number.POSITIVE_INFINITY,
        calloutSize.height,
        windowSize.height,
      );
    }),
  );
}
